import { useCallback, useMemo } from 'react';
import { Table as AntTable, Button, Empty } from 'antd';
import type { TableProps } from 'antd/es/table';
import type {
  SorterResult,
  FilterValue,
  TablePaginationConfig,
  TableCurrentDataSource,
} from 'antd/es/table/interface';
import styled from 'styled-components';
import {
  useColumnVisibility,
  IColumnVisibilityState,
} from './hooks/useColumnVisibility';
import { useColumnOrder } from './hooks/useColumnOrder';
import { useColumnWidths } from './hooks/useColumnWidths';
import {
  ITableExpandable,
  ITablePagination,
  IRowActions,
  ISortState,
  ITableColumn,
  ITableConfigState,
} from './utils/types';
import { getActiveSorter } from './utils/getActiveSorter';
import {
  applyWidths,
  applyMenu,
  applyRowActions,
  applyConfigColumn,
} from './utils/columnTransformers';
import Error from '../Error';

export interface ITableProps<T> {
  columns: ITableColumn<T>[];
  data: T[];
  rowKey?: string | ((record: T) => string);
  loading?: boolean;
  emptyText?: string;
  error?: { message?: string } | null;
  enableColumnConfig?: boolean;
  enableColumnMenu?: boolean;
  onRowClick?: (record: T) => void;
  expandable?: ITableExpandable<T>;
  rowClassName?: string | TableProps<T>['rowClassName'] | undefined;
  size?: 'small' | 'middle' | 'large';
  scroll?: TableProps<T>['scroll'];
  pagination?: ITablePagination;
  rowSelection?: TableProps<T>['rowSelection'];
  rowActions?: IRowActions<T>;
  rowActionsCallback?: (record: T) => IRowActions<T> | undefined;
  onSortChange?: (sortState: ISortState) => void;
  onFilterChange?: (filters: Record<string, FilterValue | null>) => void;
  onTableChange?: TableProps<T>['onChange'];
  onColumnOrderChange?: (order: string[]) => void;
  onColumnVisibilityChange?: (visibilityState: IColumnVisibilityState) => void;
  onColumnResize?: (columnKey: string, width: number) => void;
  onConfigSave?: (config: ITableConfigState) => void;
}

export const Table = <T extends object>({
  columns,
  data,
  rowKey = 'id',
  loading = false,
  emptyText = '',
  error,
  enableColumnConfig,
  enableColumnMenu,
  onRowClick,
  expandable,
  rowClassName,
  size,
  scroll,
  pagination,
  rowSelection,
  rowActions,
  rowActionsCallback,
  onSortChange,
  onFilterChange,
  onTableChange,
  onColumnOrderChange,
  onColumnVisibilityChange,
  onColumnResize,
  onConfigSave,
}: ITableProps<T>) => {
  const {
    orderedColumns,
    columnOrder,
    moveColumnLeft,
    moveColumnRight,
    moveColumnFirst,
    moveColumnLast,
  } = useColumnOrder(columns, onColumnOrderChange);

  const { visibilityState, setVisibility, processedColumns } =
    useColumnVisibility(orderedColumns, onColumnVisibilityChange);

  const { widthState, startResize, setLiveWidth, saveWidth, cancelResize } =
    useColumnWidths(columns, onColumnResize);

  const handleSaveConfig = useCallback(() => {
    const config: ITableConfigState = {
      columnOrder,
      columnVisibility: visibilityState,
      columnWidths: widthState,
      enableColumnMenu: enableColumnMenu ?? false,
      enableColumnConfig: enableColumnConfig ?? false,
    };
    onConfigSave?.(config);
  }, [
    columnOrder,
    visibilityState,
    widthState,
    enableColumnMenu,
    enableColumnConfig,
    onConfigSave,
  ]);

  const handleChange: TableProps<T>['onChange'] = useCallback(
    (
      _pagination: TablePaginationConfig,
      filters: Record<string, FilterValue | null>,
      sorter: SorterResult<T> | SorterResult<T>[],
      extra: TableCurrentDataSource<T>
    ) => {
      onTableChange?.(_pagination, filters, sorter, extra);

      onFilterChange?.(filters);

      if (onSortChange) {
        const activeSorter = getActiveSorter(sorter);
        if (!activeSorter) {
          return;
        }

        const columnKey =
          (activeSorter.field as string | undefined) ??
          (activeSorter.columnKey as string | undefined) ??
          null;

        if (!columnKey) {
          return;
        }

        onSortChange({
          columnKey,
          order: activeSorter.order ?? null,
        });
      }
    },
    [onTableChange, onSortChange, onFilterChange]
  );

  const handleMenuSort = useCallback(
    (columnKey: string, order: 'ascend' | 'descend') => {
      onSortChange?.({ columnKey, order });
    },
    [onSortChange]
  );

  const finalColumns = useMemo(() => {
    let result = applyWidths(processedColumns, widthState);

    if (enableColumnMenu) {
      result = applyMenu({
        columns: result,
        widthState,
        handleMenuSort,
        moveColumnLeft,
        moveColumnRight,
        moveColumnFirst,
        moveColumnLast,
        setVisibility,
        startResize,
        setLiveWidth,
        saveWidth,
        cancelResize,
      });
    }

    result = applyRowActions(result, rowActions, rowActionsCallback);

    if (enableColumnConfig) {
      result = applyConfigColumn({
        columns: result,
        originalColumns: columns,
        visibilityState,
        setVisibility,
      });
    }

    return result;
  }, [
    processedColumns,
    widthState,
    enableColumnMenu,
    handleMenuSort,
    moveColumnLeft,
    moveColumnRight,
    moveColumnFirst,
    moveColumnLast,
    setVisibility,
    startResize,
    setLiveWidth,
    saveWidth,
    cancelResize,
    rowActions,
    enableColumnConfig,
    columns,
    visibilityState,
    rowActionsCallback,
  ]);

  const rowClassNameWithOnClick = useMemo(() => {
    if (!onRowClick) return rowClassName;

    if (typeof rowClassName === 'function' && !!onRowClick) {
      return (record: T, index: number, indent: number) => {
        return `${rowClassName(record, index, indent)} clickable`;
      };
    }

    if (typeof rowClassName === 'string' && !!onRowClick) {
      return `${rowClassName} clickable`;
    }

    if (!!onRowClick) {
      return 'clickable';
    }

    return rowClassName;
  }, [rowClassName, onRowClick]);

  const expandableProps = useMemo(() => {
    return { ...expandable, columnWidth: 0 };
  }, [expandable]);

  if (error) {
    return <Error error={error.message ?? 'An unexpected error occurred'} />;
  }

  const tableElement = (
    <AntTable<T>
      columns={finalColumns}
      dataSource={data}
      rowKey={rowKey}
      loading={loading}
      pagination={pagination ?? false}
      expandable={expandableProps}
      rowClassName={rowClassNameWithOnClick}
      size={size}
      scroll={scroll}
      rowSelection={rowSelection}
      onChange={handleChange}
      locale={{
        emptyText: (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={emptyText} />
        ),
      }}
      onRow={record => ({
        onClick: () => onRowClick?.(record),
      })}
    />
  );

  const needsWrapper = enableColumnMenu || enableColumnConfig || rowActions;

  if (needsWrapper) {
    return (
      <TableWrapper $enableColumnConfig={enableColumnConfig}>
        {onConfigSave && (
          <ConfigButton onClick={handleSaveConfig}>Save Config</ConfigButton>
        )}
        {tableElement}
      </TableWrapper>
    );
  }

  return tableElement;
};

interface ITableWrapperProps {
  $enableColumnConfig?: boolean;
}

const TableWrapper = styled.div<ITableWrapperProps>`
  .ant-table-column-sorters {
    cursor: default;
  }

  .ant-table-column-sorter,
  .ant-table-column-sorter-inner {
    display: none !important;
  }

  .ant-table-column-has-sorters:hover {
    background: #f9fafc !important;
  }

  ${({ $enableColumnConfig }) =>
    $enableColumnConfig &&
    `
    .ant-table-thead > tr > th.table-header-cell--borderless::before {
      display: none !important;
    }

    .ant-table-cell.ant-table-cell-ellipsis:has(
        + .table-header-cell--borderless
      )::before {
      display: none !important;
    }

    .ant-table-thead > tr > th.table-header-cell--borderless {
      position: sticky !important;
      right: 0 !important;
      z-index: 3 !important;
      background: #f8fafc !important;
    }

    .ant-table.ant-table-ping-left .ant-table-thead > tr > th.table-header-cell--borderless {
      box-shadow: -2px 0 8px rgba(0, 0, 0, 0.08);
    }
  `}
`;

const ConfigButton = styled(Button)`
  margin-bottom: 14px;
  float: right;
`;
